import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
const PROVIDERS = [
    { key: 'smtp', label: 'SMTP', desc: 'Email OTP、系統通知、工單通知寄信設定' },
    { key: 'sms', label: 'SMS', desc: '手機 OTP 與短訊通知供應商設定（Twilio Verify）' },
    { key: 'stripe', label: 'Stripe', desc: '訂閱、Credit 加值與 webhook 設定' },
    { key: 'fal_ai', label: 'Fal.ai', desc: 'AI 圖片、海報與菜式圖片生成 Key 設定' },
    { key: 'ai_prompts', label: 'AI Prompt', desc: 'POS 端 AI 功能的全平台預設 Prompt；店舖自訂 Prompt 仍會優先使用' },
];
const AI_PROMPT_DEFINITIONS = [
    {
        key: 'menu_ocr', title: '餐牌圖片 OCR', variables: ['restaurant_name', 'cuisine_type'],
        description: '從紙本餐牌或相片抽取分類、菜品、價格與描述。',
        default_prompt: `你是餐廳菜單數位化助手。請從這張菜單圖片擷取所有可辨識的項目，輸出**純 JSON**，
結構如下（不要任何額外文字、不要 markdown）：

{
  "categories": [
    {
      "name": "分類名稱",
      "items": [
        {
          "name": "菜名",
          "price": 88.0,
          "description": "可選描述，若無則 null",
          "tags": ["招牌"|"辣"|"素食"|...]
        }
      ]
    }
  ]
}

餐廳：{restaurant_name}
菜系：{cuisine_type}

規則：
- 必須輸出正常 UTF-8 繁體中文；不可輸出亂碼、拼音或簡體中文
- price 以數字呈現（單位 HKD），無價格寫 0
- 模糊不清或不確定的項目仍輸出，但加 tag "uncertain"
- 描述精簡，不超過 80 字
- 優先使用圖片上的餐單標題或分段標題作分類名稱，例如「午餐」、「是日本餐」、「飲品」；真的沒有分類才放入 "未分類"`,
    },
    {
        key: 'menu_url_import', title: '網址餐牌匯入', variables: ['url', 'content', 'restaurant_name', 'cuisine_type'],
        description: '從 OpenRice、Keeta、Foodpanda、Inline 或店家官網文字抽取餐牌。',
        default_prompt: `你是餐飲菜單分析助手。以下是一個餐廳網頁（可能來自 OpenRice、Keeta、Foodpanda、Inline、店家官網等）的純文字內容。
請從中抽取「分類 → 菜品」結構，輸出 JSON：
{"categories":[{"name":"分類名","items":[{"name":"菜名","price":99.0,"description":"如有"}]}]}

餐廳：{restaurant_name}
菜系：{cuisine_type}
來源 URL：{url}

規則：
- 名稱保留原語言（中文/英文皆可）。
- price 必須為純數字（HKD），找不到價錢就填 0。
- 不要包含外賣費、最低消費、優惠碼、公司簡介等非菜單內容。
- 若整頁沒有可辨識菜單就回傳 {"categories": []}。
- 直接輸出 JSON，不要多餘文字。

=== 網頁內容 ===
{content}`,
    },
    {
        key: 'shop_profile_import', title: '店舖網站資料讀取', variables: ['url', 'content'],
        description: '從官網、OpenRice、Google 商家頁或外賣平台抽取店名、地址、介紹與品牌資料。',
        default_prompt: `你是香港餐廳資料整理助手。以下是一個餐廳網站或餐飲平台頁面的純文字、meta、JSON-LD、搜尋結果片段或 URL 線索。
請抽取可用於 POS 店舖資料、傳單、海報、電子菜單和 AI 文案生成的資訊，輸出 JSON。

必須輸出以下結構，缺少資料用空字串或空陣列：
{
  "name": "店名，如可辨識",
  "contact_phone": "電話",
  "contact_email": "電郵",
  "address": "完整地址",
  "cuisine_type": "餐廳類型或菜系，簡短英文 code，不確定就空字串",
  "brand_style_prompt": "適合 AI 生成海報/菜單圖片的視覺風格描述，繁體中文",
  "shop_profile": {
    "district": "香港十八區之一或可辨識地區",
    "area": "商圈/街道/商場/附近地標，例如佐敦、尖沙咀、旺角",
    "address_line2": "地址補充，例如樓層、出口、泊車提示",
    "business_hours": [{"day":"星期一","open":"11:30","close":"22:00","closed":false}],
    "short_description": "一句話介紹，不超過 40 字",
    "introduction": "店舖介紹，80-160 字",
    "brand_story": "品牌故事，如沒有就空字串",
    "signature_items": "招牌菜或主打產品，以逗號或短句列出",
    "price_range": "人均或價位",
    "service_modes": ["堂食","外賣","自取","外送","到會","包場"],
    "website": "網站 URL",
    "instagram": "Instagram URL 或帳號",
    "facebook": "Facebook URL",
    "google_maps_url": "Google Maps URL",
    "whatsapp": "WhatsApp 或電話",
    "delivery_notes": "外送/自取/訂座備註",
    "menu_intro": "可放在電子菜單首頁的開場文案，40-80 字",
    "marketing_keywords": ["關鍵字"]
  }
}

規則：
- 只輸出 JSON，不要 Markdown。
- 優先使用 Open Graph、JSON-LD、頁面標題、餐廳平台欄位、搜尋結果片段與 URL slug 中的店名/地區/菜系線索。
- 若搜尋片段提供詳細地址、電話、營業時間、人均價、座位數或服務模式，應一併抽取。
- OpenRice URL 常見格式為 /r-店名-地區-菜系-r數字；正文被反爬擋住時，仍可從 URL 推斷店名、area 和菜系，但不要編造完整地址或電話。
- 不要虛構電話、完整地址、營業時間；不確定就留空。
- 文案使用繁體中文（香港語境），不要簡體。
- business_hours 如只看到一段籠統營業時間，可輸出一筆 {"day":"每日", ...}。

來源 URL：{url}

=== 網頁內容 ===
{content}`,
    },
    {
        key: 'dish_description', title: '菜品描述生成', variables: ['restaurant_name', 'cuisine_type', 'category', 'name', 'price', 'current_description'],
        description: '為單一食品生成可放在手機點餐頁的短描述。',
        default_prompt: `你是香港餐廳菜單文案助手。請為以下菜品產生一段可直接放在手機點餐頁的繁體中文描述。

要求：
- 只輸出描述文字，不要標題、不要引號、不要 markdown。
- 30 至 55 個中文字左右。
- 語氣自然、具食慾，但不要過度誇張。
- 不要憑空加入不存在的食材、過敏原、健康功效。
- 如果菜名不清楚，描述要保守。

餐廳：{restaurant_name}
菜系：{cuisine_type}
分類：{category}
菜名：{name}
售價：{price}
現有描述：{current_description}`,
    },
    {
        key: 'dish_image', title: '菜品圖片生成 Prompt', variables: ['restaurant_name', 'cuisine_type', 'brand_style', 'category', 'name', 'price', 'item_type', 'current_description'],
        description: '控制單一菜品 AI 生圖時如何理解分類、菜系、描述、價格與品牌風格。',
        default_prompt: `你是香港餐廳的食物攝影提示詞助手。請根據以下菜品資料，生成一張真實餐牌相片應該呈現的食物內容。請特別留意「分類」對菜名的意思有重大影響，例如「朱古力」在飲品分類可能是朱古力飲品，在甜品或小食分類可能是朱古力甜品或小食。

要求：
- 只描述應出現在相片中的食物、飲品、容器、配料、質感、顏色與擺盤。
- 不要加入菜品資料沒有暗示的肉類、海鮮、酒精、品牌 Logo、文字或人物。
- 如果菜名含糊，優先用分類、菜系與現有描述判斷；仍不確定時要保守。
- 畫面要適合餐牌：真實、乾淨、有食慾，不要卡通化。

餐廳：{restaurant_name}
菜系：{cuisine_type}
品牌語氣：{brand_style}
分類：{category}
菜名：{name}
售價：{price}
類型：{item_type}
現有描述：{current_description}`,
    },
    {
        key: 'poster_design', title: '海報設計預設 Prompt', variables: ['restaurant_name', 'cuisine_type', 'title', 'body', 'prompt_direction', 'store_context', 'menu_context', 'language', 'size'],
        description: '控制海報生成的整體視覺方向、排版與品牌一致性。',
        default_prompt: `你是香港餐廳的商業海報設計師。請生成適合社交媒體與店內推廣使用的餐飲海報背景。

設計要求：
- 畫面以真實食物、餐廳氛圍或品牌質感為主，不要卡通化。
- 預留清晰文字區域，避免背景中出現亂碼、假字、Logo 或可讀文字。
- 構圖要適合手機瀏覽，主視覺明確，邊緣保留安全距離。
- 色彩要有食慾但不要過度單一，避免廉價促銷感。
- 主題、內容與 Prompt 方向是本次任務的最高優先輸入；不可忽略。
- 若有店鋪或餐牌資料，優先貼合餐廳菜系與品牌語氣；若未提供，不要在圖片或文案中產生空白店名、空白地址、占位字或假 Logo。
- 如果缺少餐廳資料，改用主題、內容、菜系或常見餐飲場景建立視覺，不要編造店名。

餐廳：{restaurant_name}
菜系：{cuisine_type}
主題：{title}
內容：{body}
Prompt 方向：{prompt_direction}
店鋪上下文：
{store_context}
餐牌上下文：
{menu_context}
語言：{language}
尺寸：{size}`,
    },
    {
        key: 'menu_design', title: '電子菜單設計預設 Prompt', variables: ['restaurant_name', 'cuisine_type', 'scope', 'menu_context', 'language', 'size'],
        description: '控制電子菜單頁面的視覺風格、資訊層級與菜品呈現方式。',
        default_prompt: `你是香港餐廳的電子菜單版面設計師。請根據現有菜單內容生成一張可直接展示給客人的電子菜單頁。

設計要求：
- 版面要像專業餐牌，不是普通廣告圖；菜名、價格與分類的資訊層級要清晰。
- 背景可有食物或餐廳質感，但不能干擾閱讀。
- 避免生成錯誤價格、亂碼、假字或過多小字；可用簡潔區塊暗示菜單排版。
- 若是單一食品，突出該食品與價格；若是套餐，突出套餐組合與時段；若是整份菜單，呈現多分類概覽。
- 適合手機或平板顯示，留足邊距。

餐廳：{restaurant_name}
菜系：{cuisine_type}
頁面類型：{scope}
菜單內容：{menu_context}
語言：{language}
尺寸：{size}`,
    },
];
const DEFAULT_CONFIG = {
    smtp: {
        host: '', port: 587, username: '', password: '', from_email: '', use_tls: true,
        email_otp_subject: 'Rainbow POS verification code',
        email_otp_body: `Hello,

Your Rainbow POS verification code is {code}.

This code expires in {ttl_minutes} minutes. If you did not request this code, you can ignore this email.

Rainbow POS Team`,
    },
    sms: {
        provider: 'twilio_verify', account_sid: '', auth_token: '', verification_service_sid: '', from_number: '', api_url: '', api_key: '', sender_id: '',
    },
    stripe: {
        mode: 'test', publishable_key: '', secret_key: '', webhook_secret: '', price_ids: {},
    },
    fal_ai: {
        api_key: '', model_image: 'fal-ai/flux/schnell', model_poster: 'openai/gpt-image-2',
    },
    ai_prompts: {
        prompts: {},
    },
};
export default function PlatformIntegrations() {
    const [rows, setRows] = useState({});
    const [active, setActive] = useState('smtp');
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState('');
    async function reload() {
        const data = await api('/platform/integrations');
        const next = {};
        for (const p of PROVIDERS) {
            const row = data.find((r) => r.provider === p.key);
            next[p.key] = row || { provider: p.key, enabled: false, config: {}, updated_at: null };
        }
        setRows(next);
    }
    useEffect(() => { reload().catch((e) => setMsg(e.message)); }, []);
    const row = rows[active] || { provider: active, enabled: false, config: {}, updated_at: null };
    const config = { ...DEFAULT_CONFIG[active], ...(row.config || {}) };
    function patchConfig(key, value) {
        setRows((prev) => ({
            ...prev,
            [active]: { ...row, config: { ...config, [key]: value } },
        }));
    }
    function patchPriceId(key, value) {
        const price_ids = { ...(config.price_ids || {}), [key]: value };
        patchConfig('price_ids', price_ids);
    }
    async function save() {
        setSaving(true);
        setMsg('');
        try {
            const saved = await api(`/platform/integrations/${active}`, {
                method: 'PUT',
                body: JSON.stringify({ enabled: row.enabled, config }),
            });
            setRows((prev) => ({ ...prev, [active]: saved }));
            setMsg('已儲存');
        }
        catch (e) {
            setMsg(e.message || '儲存失敗');
        }
        finally {
            setSaving(false);
        }
    }
    return (_jsxs("div", { children: [_jsx("h2", { children: "\u5E73\u53F0\u6574\u5408\u8A2D\u5B9A" }), _jsx("p", { className: "muted", style: { marginTop: -8 }, children: "SMTP\u3001SMS\u3001Stripe\u3001Fal.ai \u5C6C\u65BC\u5E73\u53F0\u7D1A\u4F9B\u61C9\u5546\u8A2D\u5B9A\uFF0C\u7531\u7BA1\u7406\u7AEF\u7DAD\u8B77\uFF1B\u5BC6\u9470\u56DE\u50B3\u6642\u6703\u906E\u853D\uFF0C\u7559\u7A7A\u5132\u5B58\u6703\u4FDD\u7559\u539F\u503C\u3002" }), _jsx("div", { className: "tabs", children: PROVIDERS.map((p) => (_jsx("button", { className: active === p.key ? 'tab-active' : '', onClick: () => { setActive(p.key); setMsg(''); }, children: p.label }, p.key))) }), _jsxs("div", { className: "card", children: [_jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start' }, children: [_jsxs("div", { children: [_jsx("div", { className: "card-title", children: PROVIDERS.find((p) => p.key === active)?.label }), _jsx("div", { className: "muted", children: PROVIDERS.find((p) => p.key === active)?.desc }), row.updated_at && _jsxs("div", { className: "muted", style: { marginTop: 4 }, children: ["\u66F4\u65B0\u6642\u9593\uFF1A", new Date(row.updated_at).toLocaleString('zh-HK')] })] }), _jsxs("label", { style: { display: 'flex', gap: 8, alignItems: 'center' }, children: [_jsx("input", { type: "checkbox", checked: row.enabled, onChange: (e) => setRows((prev) => ({ ...prev, [active]: { ...row, enabled: e.target.checked } })) }), "\u555F\u7528"] })] }), active === 'smtp' && _jsx(SmtpForm, { config: config, patch: patchConfig }), active === 'sms' && _jsx(SmsForm, { config: config, patch: patchConfig }), active === 'stripe' && _jsx(StripeForm, { config: config, patch: patchConfig, patchPriceId: patchPriceId }), active === 'fal_ai' && _jsx(FalAiForm, { config: config, patch: patchConfig }), active === 'ai_prompts' && _jsx(AiPromptsForm, { config: config, patch: patchConfig }), _jsxs("div", { style: { display: 'flex', gap: 10, alignItems: 'center', marginTop: 16 }, children: [_jsx("button", { className: "primary", onClick: save, disabled: saving, children: saving ? '儲存中...' : '儲存設定' }), msg && _jsx("span", { className: "muted", children: msg })] })] })] }));
}
function Field({ label, children }) {
    return _jsxs("label", { style: { display: 'flex', flexDirection: 'column', gap: 6 }, children: [_jsx("span", { className: "muted", children: label }), children] });
}
function FormGrid({ children }) {
    return _jsx("div", { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginTop: 16 }, children: children });
}
function SmtpForm({ config, patch }) {
    return (_jsxs(_Fragment, { children: [_jsxs(FormGrid, { children: [_jsx(Field, { label: "SMTP Host", children: _jsx("input", { value: config.host || '', onChange: (e) => patch('host', e.target.value) }) }), _jsx(Field, { label: "Port", children: _jsx("input", { type: "number", value: config.port || 587, onChange: (e) => patch('port', Number(e.target.value || 587)) }) }), _jsx(Field, { label: "Username", children: _jsx("input", { value: config.username || '', onChange: (e) => patch('username', e.target.value) }) }), _jsx(Field, { label: "Password", children: _jsx("input", { type: "password", value: config.password || '', placeholder: "\u7559\u7A7A\u4FDD\u7559\u539F\u5BC6\u9470", onChange: (e) => patch('password', e.target.value) }) }), _jsx(Field, { label: "From Email", children: _jsx("input", { value: config.from_email || '', onChange: (e) => patch('from_email', e.target.value) }) }), _jsxs("label", { style: { display: 'flex', gap: 8, alignItems: 'center', marginTop: 24 }, children: [_jsx("input", { type: "checkbox", checked: !!config.use_tls, onChange: (e) => patch('use_tls', e.target.checked) }), " \u4F7F\u7528 TLS"] })] }), _jsx("h3", { style: { marginTop: 24 }, children: "\u8A3B\u518A\u9A57\u8B49\u78BC Email \u6A21\u677F" }), _jsxs("p", { className: "muted", style: { marginTop: -6 }, children: ["\u53EF\u7528\u8B8A\u6578\uFF1A", '{code}', "\u3001", '{ttl_minutes}', "\u3001", '{target}', "\u3001", '{purpose}'] }), _jsx(Field, { label: "Email Subject", children: _jsx("input", { value: config.email_otp_subject || '', onChange: (e) => patch('email_otp_subject', e.target.value) }) }), _jsxs("label", { style: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }, children: [_jsx("span", { className: "muted", children: "Email Body" }), _jsx("textarea", { rows: 9, value: config.email_otp_body || '', onChange: (e) => patch('email_otp_body', e.target.value), style: { width: '100%', resize: 'vertical' } })] })] }));
}
function SmsForm({ config, patch }) {
    return (_jsxs(FormGrid, { children: [_jsx(Field, { label: "Provider", children: _jsxs("select", { value: config.provider || 'twilio_verify', onChange: (e) => patch('provider', e.target.value), children: [_jsx("option", { value: "twilio_verify", children: "Twilio Verify" }), _jsx("option", { value: "twilio", children: "Twilio Messaging" }), _jsx("option", { value: "generic_http", children: "Generic HTTP" })] }) }), _jsx(Field, { label: "Account SID", children: _jsx("input", { value: config.account_sid || '', onChange: (e) => patch('account_sid', e.target.value) }) }), _jsx(Field, { label: "Auth Token", children: _jsx("input", { type: "password", value: config.auth_token || '', placeholder: "\u7559\u7A7A\u4FDD\u7559\u539F\u5BC6\u9470", onChange: (e) => patch('auth_token', e.target.value) }) }), _jsx(Field, { label: "Verify Service SID", children: _jsx("input", { value: config.verification_service_sid || '', onChange: (e) => patch('verification_service_sid', e.target.value) }) }), _jsx(Field, { label: "From Number", children: _jsx("input", { value: config.from_number || '', onChange: (e) => patch('from_number', e.target.value) }) }), _jsx(Field, { label: "Messaging Service SID", children: _jsx("input", { value: config.messaging_service_sid || '', onChange: (e) => patch('messaging_service_sid', e.target.value) }) }), _jsx(Field, { label: "Generic API URL", children: _jsx("input", { value: config.api_url || '', onChange: (e) => patch('api_url', e.target.value) }) }), _jsx(Field, { label: "Generic API Key", children: _jsx("input", { type: "password", value: config.api_key || '', placeholder: "\u7559\u7A7A\u4FDD\u7559\u539F\u5BC6\u9470", onChange: (e) => patch('api_key', e.target.value) }) }), _jsx(Field, { label: "Sender ID", children: _jsx("input", { value: config.sender_id || '', onChange: (e) => patch('sender_id', e.target.value) }) })] }));
}
function FalAiForm({ config, patch }) {
    return (_jsxs(FormGrid, { children: [_jsx(Field, { label: "Fal.ai API Key", children: _jsx("input", { type: "password", value: config.api_key || '', placeholder: "\u7559\u7A7A\u4FDD\u7559\u539F\u5BC6\u9470", onChange: (e) => patch('api_key', e.target.value) }) }), _jsx(Field, { label: "Dish Image Model", children: _jsx("input", { value: config.model_image || 'fal-ai/flux/schnell', onChange: (e) => patch('model_image', e.target.value) }) }), _jsx(Field, { label: "Poster Model", children: _jsx("input", { value: config.model_poster || 'openai/gpt-image-2', onChange: (e) => patch('model_poster', e.target.value) }) })] }));
}
function AiPromptsForm({ config, patch }) {
    const prompts = config.prompts || {};
    function patchPrompt(key, value) {
        patch('prompts', { ...prompts, [key]: value });
    }
    function resetPrompt(key) {
        const next = { ...prompts };
        delete next[key];
        patch('prompts', next);
    }
    return (_jsxs("div", { style: { display: 'grid', gap: 12, marginTop: 16 }, children: [_jsx("p", { className: "muted", style: { margin: 0 }, children: "\u9019\u88E1\u8A2D\u5B9A\u7684\u662F\u5168\u5E73\u53F0\u7CFB\u7D71\u9810\u8A2D Prompt\u3002POS \u5E97\u8216\u82E5\u5DF2\u6709\u81EA\u8A02 Prompt\uFF0C\u6703\u7E7C\u7E8C\u512A\u5148\u4F7F\u7528\u5E97\u8216\u81EA\u8A02\u7248\u672C\uFF1B\u7559\u7A7A\u6216\u9084\u539F\u5247\u4F7F\u7528\u7A0B\u5F0F\u5167\u5EFA\u9810\u8A2D\u3002" }), AI_PROMPT_DEFINITIONS.map((item) => {
                const value = prompts[item.key] ?? item.default_prompt;
                const isPlatformCustom = Object.prototype.hasOwnProperty.call(prompts, item.key);
                return (_jsxs("div", { className: "card", style: { padding: 14, border: '1px solid #e5e7eb' }, children: [_jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }, children: [_jsxs("div", { children: [_jsx("div", { className: "card-title", style: { marginBottom: 4 }, children: item.title }), _jsx("div", { className: "muted", children: item.description }), _jsxs("div", { className: "muted", style: { marginTop: 4, fontSize: 12 }, children: ["\u53EF\u7528\u8B8A\u6578\uFF1A", item.variables.map((v) => `{${v}}`).join('、')] })] }), _jsx("span", { className: `pill ${isPlatformCustom ? 'score-green' : ''}`, children: isPlatformCustom ? '平台自訂' : '內建預設' })] }), _jsx("textarea", { rows: 10, value: value, onChange: (e) => patchPrompt(item.key, e.target.value), style: { width: '100%', resize: 'vertical', marginTop: 10, fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace', fontSize: 13 } }), _jsx("div", { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }, children: _jsx("button", { type: "button", onClick: () => resetPrompt(item.key), children: "\u9084\u539F\u5167\u5EFA\u9810\u8A2D" }) })] }, item.key));
            })] }));
}
function StripeForm({ config, patch, patchPriceId }) {
    const prices = config.price_ids || {};
    const priceKeys = [
        'starter_monthly', 'starter_yearly',
        'pro_monthly', 'pro_yearly',
        'premium_monthly', 'premium_yearly',
        'credit_pack_1000', 'credit_pack_2000',
    ];
    return (_jsxs(_Fragment, { children: [_jsxs(FormGrid, { children: [_jsx(Field, { label: "Mode", children: _jsxs("select", { value: config.mode || 'test', onChange: (e) => patch('mode', e.target.value), children: [_jsx("option", { value: "test", children: "Test" }), _jsx("option", { value: "live", children: "Live" })] }) }), _jsx(Field, { label: "Publishable Key", children: _jsx("input", { value: config.publishable_key || '', onChange: (e) => patch('publishable_key', e.target.value) }) }), _jsx(Field, { label: "Secret Key", children: _jsx("input", { type: "password", value: config.secret_key || '', placeholder: "\u7559\u7A7A\u4FDD\u7559\u539F\u5BC6\u9470", onChange: (e) => patch('secret_key', e.target.value) }) }), _jsx(Field, { label: "Webhook Secret", children: _jsx("input", { type: "password", value: config.webhook_secret || '', placeholder: "\u7559\u7A7A\u4FDD\u7559\u539F\u5BC6\u9470", onChange: (e) => patch('webhook_secret', e.target.value) }) })] }), _jsx("h3", { children: "Stripe Price IDs" }), _jsx(FormGrid, { children: priceKeys.map((key) => (_jsx(Field, { label: key, children: _jsx("input", { value: prices[key] || '', onChange: (e) => patchPriceId(key, e.target.value) }) }, key))) })] }));
}
